import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus, Users, Search, Edit2, Trash2, ArrowRightLeft } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function DisciplesPage({ user }) {
  const [disciples, setDisciples] = useState([]);
  const [filteredDisciples, setFilteredDisciples] = useState([]);
  const [leaders, setLeaders] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDisciple, setEditingDisciple] = useState(null);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [discipleToMove, setDiscipleToMove] = useState(null);
  const [targetGeneration, setTargetGeneration] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    surname: "",
    date_of_birth: "",
    nationality: "",
    marital_status: "",
    church_joining_date: "",
    department: "",
    email: "",
    phone: "",
    address: "",
    photo: "",
    parent_disciple_id: "",
    generation: ""
  });

  useEffect(() => {
    fetchDisciples();
    if (user.role === 'super_admin') {
      fetchLeaders();
    }
  }, []);

  useEffect(() => {
    const filtered = disciples.filter(
      (d) =>
        d.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        d.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredDisciples(filtered);
  }, [searchTerm, disciples]);

  const fetchDisciples = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/disciples`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDisciples(response.data);
      setFilteredDisciples(response.data);
    } catch (error) {
      toast.error("Failed to fetch disciples");
    }
  };

  const fetchLeaders = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeaders(response.data.filter(u => u.role === 'homecell_leader'));
    } catch (error) {
      console.error("Failed to fetch leaders");
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, photo: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem("token");
      if (editingDisciple) {
        await axios.put(`${API}/disciples/${editingDisciple.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Disciple updated successfully");
      } else {
        await axios.post(`${API}/disciples`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Disciple added successfully");
      }
      fetchDisciples();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save disciple");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this disciple?")) return;

    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/disciples/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Disciple deleted successfully");
      fetchDisciples();
    } catch (error) {
      toast.error("Failed to delete disciple");
    }
  };

  const openMoveDialog = (disciple) => {
    setDiscipleToMove(disciple);
    setTargetGeneration(disciple.generation.toString());
    setMoveDialogOpen(true);
  };

  const handleMoveGeneration = async () => {
    if (!targetGeneration || targetGeneration === discipleToMove.generation.toString()) {
      toast.error("Please select a different generation");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(
        `${API}/disciples/${discipleToMove.id}/move-generation?target_generation=${targetGeneration}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      setMoveDialogOpen(false);
      setDiscipleToMove(null);
      setTargetGeneration("");
      fetchDisciples();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to move disciple");
    }
  };

  const handleFixGenerations = async () => {
    if (!window.confirm("This will recalculate and fix generation numbers for all disciples based on their creation order. First 12 disciples = Gen 1, next 12 = Gen 2, etc. Continue?")) {
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/disciples/fix-generations`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const details = response.data.details || {};
      const detailsText = Object.entries(details)
        .map(([leader, info]) => `${leader}: ${info.updated} disciples updated`)
        .join('\n');
      
      toast.success(`${response.data.message}\n${response.data.total_disciples_updated} disciples updated across ${response.data.leaders_processed} leader(s)`);
      
      if (detailsText) {
        console.log("Generation Fix Details:\n", detailsText);
      }
      
      fetchDisciples();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to fix generations");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      surname: "",
      date_of_birth: "",
      nationality: "",
      marital_status: "",
      church_joining_date: "",
      department: "",
      email: "",
      phone: "",
      address: "",
      photo: "",
      parent_disciple_id: "",
      generation: ""
    });
    setEditingDisciple(null);
  };

  const openEditDialog = (disciple) => {
    setEditingDisciple(disciple);
    setFormData({
      name: disciple.name,
      surname: disciple.surname || "",
      date_of_birth: disciple.date_of_birth || "",
      nationality: disciple.nationality || "",
      marital_status: disciple.marital_status || "",
      church_joining_date: disciple.church_joining_date || "",
      department: disciple.department || "",
      email: disciple.email || "",
      phone: disciple.phone || "",
      address: disciple.address || "",
      photo: disciple.photo || "",
      parent_disciple_id: disciple.parent_disciple_id || "",
      generation: disciple.generation || ""
    });
    setDialogOpen(true);
  };

  const getGenerationStats = () => {
    const stats = {};
    disciples.forEach((d) => {
      stats[d.generation] = (stats[d.generation] || 0) + 1;
    });
    return stats;
  };

  const generationStats = getGenerationStats();

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          <span className="gradient-text">Disciples</span>
        </h1>
        <p className="text-gray-600">Manage your discipleship network</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/80 text-sm">Total Disciples</p>
              <p className="text-3xl font-bold" data-testid="total-disciples">{disciples.length}</p>
            </div>
            <Users className="h-12 w-12 text-white/40" />
          </div>
        </div>

        {Object.entries(generationStats).slice(0, 2).map(([gen, count]) => (
          <div key={gen} className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Generation {gen}</p>
                <p className="text-3xl font-bold text-gray-800">{count}</p>
              </div>
              <div className="generation-badge">Gen {gen}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="card mb-6">
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="relative w-full md:w-96">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <Input
              data-testid="search-disciples"
              placeholder="Search disciples..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          <div className="flex gap-2">
            <Button
              onClick={handleFixGenerations}
              variant="outline"
              className="border-orange-500 text-orange-600 hover:bg-orange-50"
              disabled={loading}
            >
              <Users className="mr-2 h-4 w-4" />
              Fix Generations
            </Button>
            
            <Dialog open={dialogOpen} onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button data-testid="add-disciple-button" className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700">
                  <Plus className="mr-2 h-4 w-4" /> Add Disciple
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingDisciple ? "Edit Disciple" : "Add New Disciple"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Name *</Label>
                    <Input
                      id="name"
                      data-testid="disciple-name-input"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="John"
                    />
                  </div>
                  <div>
                    <Label htmlFor="surname">Surname *</Label>
                    <Input
                      id="surname"
                      data-testid="disciple-surname-input"
                      required
                      value={formData.surname}
                      onChange={(e) => setFormData({ ...formData, surname: e.target.value })}
                      placeholder="Smith"
                    />
                  </div>
                  <div>
                    <Label htmlFor="date_of_birth">Date of Birth</Label>
                    <Input
                      id="date_of_birth"
                      data-testid="disciple-dob-input"
                      type="date"
                      value={formData.date_of_birth}
                      onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="nationality">Nationality</Label>
                    <Input
                      id="nationality"
                      data-testid="disciple-nationality-input"
                      value={formData.nationality}
                      onChange={(e) => setFormData({ ...formData, nationality: e.target.value })}
                      placeholder="e.g., American"
                    />
                  </div>
                  <div>
                    <Label htmlFor="marital_status">Marital Status</Label>
                    <select
                      id="marital_status"
                      data-testid="disciple-marital-input"
                      className="w-full p-2 border rounded-lg"
                      value={formData.marital_status}
                      onChange={(e) => setFormData({ ...formData, marital_status: e.target.value })}
                    >
                      <option value="">Select status</option>
                      <option value="Single">Single</option>
                      <option value="Married">Married</option>
                      <option value="Divorced">Divorced</option>
                      <option value="Widowed">Widowed</option>
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="church_joining_date">Church Joining Date</Label>
                    <Input
                      id="church_joining_date"
                      data-testid="disciple-joining-date-input"
                      type="date"
                      value={formData.church_joining_date}
                      onChange={(e) => setFormData({ ...formData, church_joining_date: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="department">Department</Label>
                    <Input
                      id="department"
                      data-testid="disciple-department-input"
                      value={formData.department}
                      onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                      placeholder="e.g., Youth Ministry"
                    />
                  </div>
                  <div>
                    <Label htmlFor="generation">Generation</Label>
                    <Select 
                      value={formData.generation?.toString() || "auto"} 
                      onValueChange={(val) => setFormData({ ...formData, generation: val === "auto" ? null : parseInt(val) })}
                    >
                      <SelectTrigger id="generation" data-testid="disciple-generation-select">
                        <SelectValue placeholder="Auto-assign (recommended)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="auto">Auto-assign (recommended)</SelectItem>
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((gen) => {
                          const genStats = getGenerationStats();
                          const count = genStats[gen] || 0;
                          const isFull = count >= 12;
                          return (
                            <SelectItem key={gen} value={gen.toString()} disabled={isFull && (!editingDisciple || editingDisciple.generation !== gen)}>
                              Generation {gen} ({count}/12) {isFull ? "- Full" : ""}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500 mt-1">
                      Leave as auto-assign to place in next available slot. Max 12 per generation.
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      data-testid="disciple-email-input"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      data-testid="disciple-phone-input"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="parent">Parent Disciple (Optional)</Label>
                    <select
                      id="parent"
                      data-testid="parent-disciple-select"
                      className="w-full p-2 border rounded-lg"
                      value={formData.parent_disciple_id}
                      onChange={(e) => setFormData({ ...formData, parent_disciple_id: e.target.value })}
                    >
                      <option value="">None (Generation 1)</option>
                      {disciples.map((d) => (
                        <option key={d.id} value={d.id}>
                          {d.name} {d.surname} (Gen {d.generation})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="address">Address</Label>
                  <Input
                    id="address"
                    data-testid="disciple-address-input"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  />
                </div>

                <div>
                  <Label htmlFor="photo">Photo</Label>
                  <div className="flex items-center gap-4">
                    <Input
                      id="photo"
                      data-testid="disciple-photo-input"
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoChange}
                      className="flex-1"
                    />
                    {formData.photo && (
                      <img src={formData.photo} alt="Preview" className="h-16 w-16 rounded-lg object-cover" />
                    )}
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button data-testid="save-disciple-button" type="submit" disabled={loading} className="bg-gradient-to-r from-purple-600 to-indigo-600">
                    {loading ? "Saving..." : editingDisciple ? "Update" : "Save"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          {/* Move Generation Dialog */}
          <Dialog open={moveDialogOpen} onOpenChange={setMoveDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Move Disciple to Different Generation</DialogTitle>
              </DialogHeader>
              {discipleToMove && (
                <div className="space-y-4">
                  <div className="p-4 bg-purple-50 rounded-lg">
                    <p className="font-semibold">{discipleToMove.name} {discipleToMove.surname}</p>
                    <p className="text-sm text-gray-600">Current Generation: {discipleToMove.generation}</p>
                  </div>

                  <div>
                    <Label htmlFor="target-generation">Move to Generation</Label>
                    <Select value={targetGeneration} onValueChange={setTargetGeneration}>
                      <SelectTrigger id="target-generation">
                        <SelectValue placeholder="Select generation" />
                      </SelectTrigger>
                      <SelectContent>
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((gen) => (
                          <SelectItem key={gen} value={gen.toString()}>
                            Generation {gen}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500 mt-1">
                      Note: Each generation can contain a maximum of 12 disciples
                    </p>
                  </div>

                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setMoveDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button 
                      onClick={handleMoveGeneration}
                      className="bg-gradient-to-r from-purple-600 to-indigo-600"
                    >
                      Move Disciple
                    </Button>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>
          </div>
        </div>
      </div>

      {user.role === 'super_admin' ? (
        // Super Admin: Group disciples by homecell leader
        <div className="space-y-8">
          {leaders.map(leader => {
            const leaderDisciples = filteredDisciples.filter(d => d.homecell_leader_id === leader.id);
            if (leaderDisciples.length === 0) return null;
            
            // Group by generation
            const generationGroups = {};
            leaderDisciples.forEach(d => {
              if (!generationGroups[d.generation]) {
                generationGroups[d.generation] = [];
              }
              generationGroups[d.generation].push(d);
            });
            
            return (
              <div key={leader.id} className="card border-l-4 border-purple-500">
                <div className="mb-4">
                  <div className="flex items-center gap-3 mb-2">
                    <Users className="h-6 w-6 text-purple-600" />
                    <h2 className="text-2xl font-bold text-purple-700">{leader.name}</h2>
                  </div>
                  <p className="text-gray-600 ml-9">
                    {leader.homecell_name || "Homecell"} • {leaderDisciples.length} Disciples
                  </p>
                </div>
                
                {Object.entries(generationGroups).sort(([a], [b]) => a - b).map(([gen, genDisciples]) => (
                  <div key={gen} className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-700 mb-3 ml-9">
                      Generation {gen} ({genDisciples.length}/12)
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 ml-9">
                      {genDisciples.map((disciple) => (
                        <div key={disciple.id} className="disciple-card" data-testid={`disciple-card-${disciple.id}`}>
                          <div className="flex items-start gap-4">
                            <div className="h-16 w-16 rounded-full bg-gradient-to-br from-purple-400 to-indigo-500 flex items-center justify-center overflow-hidden flex-shrink-0">
                              {disciple.photo ? (
                                <img src={disciple.photo} alt={disciple.name} className="h-full w-full object-cover" />
                              ) : (
                                <span className="text-white text-xl font-bold">{disciple.name[0]}</span>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between">
                                <div className="min-w-0">
                                  <h3 className="font-semibold text-lg truncate">{disciple.name} {disciple.surname}</h3>
                                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                                    <div className="generation-badge">Gen {disciple.generation}</div>
                                    {disciple.department && (
                                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">{disciple.department}</span>
                                    )}
                                  </div>
                                </div>
                                <div className="flex gap-2 ml-2">
                                  <button
                                    data-testid={`move-disciple-${disciple.id}`}
                                    onClick={() => openMoveDialog(disciple)}
                                    className="text-purple-600 hover:text-purple-700 p-1"
                                    title="Move to different generation"
                                  >
                                    <ArrowRightLeft className="h-4 w-4" />
                                  </button>
                                  <button
                                    data-testid={`edit-disciple-${disciple.id}`}
                                    onClick={() => openEditDialog(disciple)}
                                    className="text-blue-600 hover:text-blue-700 p-1"
                                  >
                                    <Edit2 className="h-4 w-4" />
                                  </button>
                                  <button
                                    data-testid={`delete-disciple-${disciple.id}`}
                                    onClick={() => handleDelete(disciple.id)}
                                    className="text-red-600 hover:text-red-700 p-1"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </div>
                              </div>
                              <div className="mt-2 space-y-1">
                                {disciple.email && <p className="text-sm text-gray-600 truncate">{disciple.email}</p>}
                                {disciple.phone && <p className="text-sm text-gray-600">{disciple.phone}</p>}
                                {disciple.marital_status && <p className="text-sm text-gray-600">Status: {disciple.marital_status}</p>}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      ) : (
        // Homecell Leader: Show own disciples grouped by generation
        <div className="space-y-6">
          {(() => {
            const generationGroups = {};
            filteredDisciples.forEach(d => {
              if (!generationGroups[d.generation]) {
                generationGroups[d.generation] = [];
              }
              generationGroups[d.generation].push(d);
            });
            
            return Object.entries(generationGroups).sort(([a], [b]) => a - b).map(([gen, genDisciples]) => (
              <div key={gen} className="card border-l-4 border-purple-500">
                <h3 className="text-xl font-bold text-purple-700 mb-4">
                  Generation {gen} ({genDisciples.length}/12)
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {genDisciples.map((disciple) => (
                    <div key={disciple.id} className="disciple-card" data-testid={`disciple-card-${disciple.id}`}>
                      <div className="flex items-start gap-4">
                        <div className="h-16 w-16 rounded-full bg-gradient-to-br from-purple-400 to-indigo-500 flex items-center justify-center overflow-hidden flex-shrink-0">
                          {disciple.photo ? (
                            <img src={disciple.photo} alt={disciple.name} className="h-full w-full object-cover" />
                          ) : (
                            <span className="text-white text-xl font-bold">{disciple.name[0]}</span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between">
                            <div className="min-w-0">
                              <h3 className="font-semibold text-lg truncate">{disciple.name} {disciple.surname}</h3>
                              <div className="flex items-center gap-2 mt-1 flex-wrap">
                                <div className="generation-badge">Gen {disciple.generation}</div>
                                {disciple.department && (
                                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">{disciple.department}</span>
                                )}
                              </div>
                            </div>
                            <div className="flex gap-2 ml-2">
                              <button
                                data-testid={`move-disciple-${disciple.id}`}
                                onClick={() => openMoveDialog(disciple)}
                                className="text-purple-600 hover:text-purple-700 p-1"
                                title="Move to different generation"
                              >
                                <ArrowRightLeft className="h-4 w-4" />
                              </button>
                              <button
                                data-testid={`edit-disciple-${disciple.id}`}
                                onClick={() => openEditDialog(disciple)}
                                className="text-blue-600 hover:text-blue-700 p-1"
                              >
                                <Edit2 className="h-4 w-4" />
                              </button>
                              <button
                                data-testid={`delete-disciple-${disciple.id}`}
                                onClick={() => handleDelete(disciple.id)}
                                className="text-red-600 hover:text-red-700 p-1"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                          <div className="mt-2 space-y-1">
                            {disciple.email && <p className="text-sm text-gray-600 truncate">{disciple.email}</p>}
                            {disciple.phone && <p className="text-sm text-gray-600">{disciple.phone}</p>}
                            {disciple.marital_status && <p className="text-sm text-gray-600">Status: {disciple.marital_status}</p>}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ));
          })()}
        </div>
      )}

      {filteredDisciples.length === 0 && (
        <div className="text-center py-12">
          <Users className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No disciples found. Add your first disciple to get started!</p>
        </div>
      )}
    </div>
  );
}
